/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import { Typography } from '@/components/typography';
import { routes } from '@/route-paths';
import { FulfillmentGroupCard } from './fulfillment-group-card';
import { OrderInfoCard } from './order-info-card';
import { OrderTotalsCard } from './order-totals-card';
import { collectFulfillmentGroups, formatOrderedDateTime, presentField } from './helpers';
import type { SomOrderModel } from '@/lib/order/guest-lookup.server';

export interface TrackOrderSummaryViewProps {
    order: SomOrderModel;
    /**
     * Optional action bar (cancel / return entry points + any post-mutation panels), rendered
     * directly under the header. Supplied by {@link TrackOrderResult}; omitted for the pure
     * read-only view.
     */
    actions?: ReactNode;
}

/**
 * The read-only guest order view rendered from a successful `/track-order` lookup. Collapses
 * SFRA's Order-GuestDetails → Order-Details drill-down into one page: header (order number +
 * date), fulfillment groups (items + status + tracking), shipping/billing/payment info, and
 * money totals. Purely presentational — it receives the already-fetched SOM model and never
 * touches the `orderToken` (which the route holds in React state for later cancel/return).
 */
export function TrackOrderSummaryView({ order, actions }: TrackOrderSummaryViewProps) {
    const { t, i18n } = useTranslation('trackOrder');

    const groups = useMemo(() => collectFulfillmentGroups(order), [order]);
    const { date, time } = formatOrderedDateTime(order.orderedDate, i18n.language);
    const orderNumber = presentField(order.sfccOrderNumber) ?? presentField(order.id);
    const orderedOn = [date, time].filter(Boolean).join(' · ');

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <Typography variant="h3" as="h1">
                        {t('result.title')}
                    </Typography>
                    {orderNumber ? (
                        <p className="mt-1 text-sm text-muted-foreground">
                            {t('result.orderNumber')}: <span className="text-foreground">{orderNumber}</span>
                        </p>
                    ) : null}
                    {orderedOn ? (
                        <p className="text-sm text-muted-foreground">
                            {t('result.orderDate')}: <span className="text-foreground">{orderedOn}</span>
                        </p>
                    ) : null}
                </div>

                <Link to={routes.trackOrder} className="text-sm font-medium text-primary hover:text-primary/80">
                    {t('result.lookupAnother')}
                </Link>
            </div>

            {actions}

            {groups.map((group, index) => (
                <FulfillmentGroupCard key={group.id ?? `group-${index}`} group={group} />
            ))}

            <OrderInfoCard order={order} />
            <OrderTotalsCard totals={order.totals} />
        </div>
    );
}
